/**
 * ATL-889 — count the backend writes that are currently in flight, so a live rollback does not
 * restart the app on top of one.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS
 *
 * The dual-write commits to Firestore FIRST and then awaits the Supabase call. The ATL-782 durable
 * outbox only enqueues on an **observed rejection**.
 *
 * ATL-888's live rollback restarts the app when an org's read flag is pulled. A restart tears down
 * the JS runtime, which produces no rejection — so a sale whose Firestore batch has committed while
 * its Supabase mirror is still in flight loses the mirror with **no rejection, no outbox entry and
 * no Sentry event**. Silently, on every busy till in the org, at the exact moment of an incident.
 *
 * That is the same drift class the migration campaign has spent weeks eliminating (ATL-871: sales
 * present in Firestore and absent from Supabase). Manufacturing it from the rollback path would be
 * a self-inflicted version of the bug the rollback exists to contain.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHY A COUNTER AND NOT A QUEUE
 *
 * The rollback does not need to know WHAT is in flight, only WHETHER anything is. A counter is
 * exact for that question, costs one increment per write, and cannot itself fail in a way that
 * loses data. `finally` guarantees the decrement, so a rejected or timed-out write is not counted
 * forever — those paths reach the outbox on their own.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE DRAIN IS CAPPED, AND THAT IS THE POINT
 *
 * A kill switch that a hung request can veto is not a kill switch. `waitForWritesToDrain` therefore
 * returns after `capMs` whether or not the count reached zero, and reports which happened — so
 * "we waited and it drained" and "we gave up and restarted anyway" are never spelled the same way.
 * The caller restarts either way; the difference is what gets logged.
 */

/** Writes currently awaiting a response. Module-scoped: one JS runtime is one till. */
let inFlight = 0

/** Highest concurrent count seen this session — useful when reading a drain timeout. */
let peakInFlight = 0

/**
 * Mark a write as started. **Always pair with the returned function in a `finally`**, or the count
 * leaks upward and every subsequent drain waits the full cap.
 *
 * @returns the matching `end`. Idempotent, so a double-call cannot push the count negative.
 */
export const beginWrite = (): (() => void) => {
  inFlight += 1
  if (inFlight > peakInFlight) peakInFlight = inFlight

  let ended = false
  return () => {
    if (ended) return
    ended = true
    // `Math.max` is belt-and-braces against a stray end from a path this module cannot see; a
    // negative count would make `waitForWritesToDrain` return instantly and defeat the guard.
    inFlight = Math.max(0, inFlight - 1)
  }
}

/** How many backend writes are awaiting a response right now. */
export const writesInFlight = (): number => inFlight

/** Highest concurrent write count seen this session. */
export const peakWritesInFlight = (): number => peakInFlight

/** TEST-ONLY: reset the module counters (they are session-scoped by design). */
export const resetWritesInFlightForTests = (): void => {
  inFlight = 0
  peakInFlight = 0
}

export interface DrainResult {
  /** True if the count reached zero before the cap. */
  drained: boolean
  /** Writes still in flight when this returned — non-zero only when `drained` is false. */
  remaining: number
  /** Roughly how long the wait took, in ms. */
  waitedMs: number
}

export interface WaitForWritesToDrainParams {
  /**
   * Hard ceiling on the wait. The caller restarts when this expires regardless — a stuck request
   * must never be able to veto a rollback.
   */
  capMs?: number
  pollMs?: number
  /** Injected for tests. */
  now?: () => number
  sleep?: (ms: number) => Promise<void>
  readInFlight?: () => number
}

/**
 * Wait for in-flight backend writes to finish, up to `capMs`.
 *
 * Never throws and never waits indefinitely.
 */
export const waitForWritesToDrain = async ({
  capMs = 3000,
  pollMs = 50,
  now = Date.now,
  sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)),
  readInFlight = writesInFlight,
}: WaitForWritesToDrainParams = {}): Promise<DrainResult> => {
  const startedAt = now()

  // Fast path: nothing in flight, so do not delay a rollback by even one poll interval.
  if (readInFlight() === 0) {
    return { drained: true, remaining: 0, waitedMs: 0 }
  }

  // TWO independent stop conditions, on purpose.
  //
  // The clock bound is the intended one. The iteration bound exists because the clock bound alone is
  // only as trustworthy as `now()`: if it ever returns a frozen or non-monotonic value — a clock
  // adjustment, a suspended device resuming, an injected fake — `now() - startedAt < capMs` stays
  // true forever, and because this loop awaits an immediately-resolving `sleep` it would spin as a
  // microtask loop that STARVES THE EVENT LOOP. The app would hang rather than restart, turning a
  // safety improvement into a worse outage than the one it was guarding.
  //
  // `+2` so a legitimate cap/poll ratio can never be cut short by rounding.
  const maxIterations = Math.ceil(capMs / Math.max(1, pollMs)) + 2
  let iterations = 0

  while (now() - startedAt < capMs && iterations < maxIterations) {
    iterations += 1
    await sleep(pollMs)
    if (readInFlight() === 0) {
      return { drained: true, remaining: 0, waitedMs: now() - startedAt }
    }
  }

  return { drained: false, remaining: readInFlight(), waitedMs: now() - startedAt }
}
